import { useMemo } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import LockClockOutlined from "@mui/icons-material/LockClockOutlined";
import TimerOffOutlined from "@mui/icons-material/TimerOffOutlined";
import WifiOffRounded from "@mui/icons-material/WifiOffRounded";
import { QrCode, playerHandleSuffix } from "@bw/shared/components";
import { useAuth } from "@bw/shared/hooks";
import type { DropInSessionResponse } from "@bw/shared/types/generated";
import { canonicalizeDropInPaymentOptions } from "@bw/shared/utils";
import { CHECK_IN_CODE_SHADOW } from "../../../app/theme/checkIn";
import { DetailHeader } from "../../../common/components/DetailHeader";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { useMyBookings } from "../../booking/hooks/useMyBookings";
import { BookedBy } from "../components/BookedBy";
import { CheckInCard, CheckInRow, VisitDots } from "../components/CheckInCard";
import { useDropInPaymentOptions } from "../hooks/useDropInPaymentOptions";
import { useFacilityMode } from "../hooks/useFacilityMode";
import { useQueueStats } from "../hooks/useQueueStats";
import { useRedemptionCode, type RedemptionSummary } from "../hooks/useRedemptionCode";
import {
  REDEMPTION_REFRESH_MS,
  endedMessage,
  formatFacilityTime,
  isLaterToday,
  redemptionErrorView,
  secondsUntil,
  visitDots,
} from "../lib/redemption";

const okPillSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  height: 24,
  px: 1.125,
  borderRadius: "4px",
  fontSize: "0.78rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  lineHeight: 1,
  bgcolor: "semantic.selected",
  color: "semantic.onSelected",
} as const;

/** The refreshing-code countdown ring: drains over the 30 s refresh period. */
function RefreshRing({ seconds }: { seconds: number }) {
  const value = Math.min(100, (seconds / (REDEMPTION_REFRESH_MS / 1000)) * 100);
  return (
    <CircularProgress
      aria-hidden
      variant="determinate"
      enableTrackSlot
      value={value}
      size={22}
      thickness={5}
      sx={{ flex: "none", color: "primary.main", "& .MuiCircularProgress-track": { color: "divider" } }}
    />
  );
}

function useNameLine() {
  const { user } = useAuth();
  const profileQ = useMyProfile();
  const name = profileQ.data?.display_name?.trim() || user?.displayName?.trim() || "";
  const tag = user?.uid ? playerHandleSuffix(user.uid) : "";
  return { name, tag };
}

function WillUseCard({
  redemption,
  totalVisits,
  facilityName,
}: {
  redemption: RedemptionSummary;
  totalVisits: number | null;
  facilityName: string;
}) {
  const left = redemption.unitsLeft;
  const dots =
    left !== null && totalVisits !== null ? visitDots(totalVisits, left) : null;
  return (
    <CheckInCard title="This code will use" titleId="code-will-use">
      {redemption.kind === "package" && left !== null ? (
        <CheckInRow
          alignTop
          icon={<ConfirmationNumberOutlined sx={{ color: "primary.main" }} />}
          title={`1 visit from ${redemption.planName}`}
          detail={
            totalVisits !== null
              ? `${left} of ${totalVisits} left now, ${Math.max(0, left - 1)} after the scan`
              : `${left} left now, ${Math.max(0, left - 1)} after the scan`
          }
          extra={
            dots ? (
              <VisitDots dots={dots} label={`${left} of ${totalVisits} visits left`} />
            ) : null
          }
        />
      ) : (
        <CheckInRow
          icon={<ConfirmationNumberOutlined sx={{ color: "primary.main" }} />}
          title={`Drop-in with ${redemption.planName}`}
          detail="Unlimited · no visit is used"
        />
      )}
      <CheckInRow
        icon={<LockClockOutlined sx={{ color: "text.secondary" }} />}
        title={
          <Box component="span" sx={{ fontWeight: 500 }}>
            Single use{facilityName ? ` · ${facilityName}` : ""} · today
          </Box>
        }
      />
    </CheckInCard>
  );
}

function CheckedInView({
  facilityId,
  facilityName,
  redemption,
  session,
  totalVisits,
}: {
  facilityId: string;
  facilityName: string;
  redemption: RedemptionSummary;
  session: DropInSessionResponse;
  totalVisits: number | null;
}) {
  const facilityQ = useFacilityMode(facilityId);
  const myUserId = useAuth().user?.uid;
  const statsQ = useQueueStats(facilityId, true);
  const bookingsQ = useMyBookings(facilityId);
  const timeZone = facilityQ.data?.timezone ?? undefined;
  const selfServe = facilityQ.data?.queue_assignment_mode === "self_serve";
  const pending = session.status === "pending_validation";
  const checkedInAt = session.checked_in_at
    ? formatFacilityTime(session.checked_in_at, timeZone)
    : "";
  const left = redemption.unitsLeft;
  const usage =
    redemption.kind === "package" && left !== null
      ? totalVisits !== null
        ? `1 visit used, ${Math.max(0, left - 1)} of ${totalVisits} left`
        : `1 visit used, ${Math.max(0, left - 1)} left`
      : `${redemption.planName}, no visit used`;
  const stats = statsQ.data;
  const waiting =
    stats && !selfServe
      ? `${stats.queue_size} waiting${
          stats.avg_wait_minutes !== null && stats.avg_wait_minutes !== undefined
            ? ` · about ${Math.round(Number(stats.avg_wait_minutes))} min`
            : ""
        }`
      : stats
        ? `${stats.courts_available} of ${stats.courts_total} courts free`
        : "Drop-in";
  const laterBookings = useMemo(() => {
    const now = Date.now();
    return (bookingsQ.data ?? []).filter(
      (booking) =>
        booking.status === "confirmed" &&
        isLaterToday(booking.start_time, booking.end_time, now, timeZone),
    );
  }, [bookingsQ.data, timeZone]);

  return (
    <>
      <Stack spacing={1} role="status" sx={{ alignItems: "center", textAlign: "center", pt: 3.75 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "semantic.selected",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckCircleRounded aria-hidden sx={{ fontSize: 40, color: "primary.main" }} />
        </Box>
        <Typography component="h1" sx={{ fontSize: "1.3125rem", fontWeight: 700, mt: 0.75, lineHeight: 1.3 }}>
          You&apos;re checked in{facilityName ? ` at ${facilityName}` : ""}
        </Typography>
        <Typography sx={{ fontSize: "0.84rem", color: "text.secondary" }}>
          {checkedInAt ? `${checkedInAt} at the entrance kiosk · ` : ""}
          {usage}
        </Typography>
      </Stack>

      <Box sx={{ mt: 1.75 }}>
        <CheckInCard title="Now open to you" titleId="checked-in-open">
          <CheckInRow
            icon={<GroupsOutlined sx={{ color: "primary.main" }} />}
            title="Drop-in"
            detail={waiting}
            trailing={
              <Box component="span" sx={{ ...okPillSx, height: 19, px: 0.75, fontSize: "0.72rem" }}>
                {pending ? "Staff to confirm" : "Unlocked"}
              </Box>
            }
          />
          {laterBookings.map((booking) => (
            <CheckInRow
              key={booking.booking_id}
              icon={<EventAvailableOutlined sx={{ color: "info.main" }} />}
              title={`${booking.court_name ?? "Court"} · ${formatFacilityTime(booking.start_time, timeZone)}`}
              detail={<BookedBy booking={booking} myUserId={myUserId} suffix="scan its code then" />}
            />
          ))}
        </CheckInCard>
      </Box>

      <Box sx={{ flex: 1 }} />
      {pending ? (
        <Alert severity="info">
          This facility confirms drop-in check-ins at the front desk. Show this
          screen there to start playing.
        </Alert>
      ) : null}
      <Button
        component={RouterLink}
        to={selfServe ? `/dropin/${facilityId}/court-board` : `/dropin/${facilityId}/session`}
        variant="contained"
        disableElevation
        startIcon={<GroupsOutlined />}
        sx={{ height: 50, fontSize: "1rem", borderRadius: "12px", fontWeight: 600 }}
      >
        {selfServe ? "Pick a court" : "Join the drop-in queue"}
      </Button>
      <Button
        component={RouterLink}
        to={`/dropin/${facilityId}/session#sec-group`}
        variant="outlined"
        color="inherit"
        sx={{ height: 42, borderColor: "divider", fontWeight: 600 }}
      >
        Join with a friend
      </Button>
      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", textAlign: "center" }}>
        {redemption.kind === "package"
          ? "Not you, or by mistake? Tell the desk; Undo gives the visit back."
          : "Not you, or by mistake? Tell the front desk."}
      </Typography>
    </>
  );
}

/**
 * Player app › Check-in code (approved frames, #2691). After "Redeem one": a
 * refreshing single-use code (about every 30 s, so a screenshot is useless)
 * that names what it will use. Leaving without scanning uses nothing. Once a
 * kiosk scans it, the same screen turns into "You're checked in".
 */
export function CheckInCodePage() {
  const { facilityId = "", subscriptionId = "" } = useParams<{
    facilityId: string;
    subscriptionId: string;
  }>();
  const navigate = useNavigate();
  const { name, tag } = useNameLine();
  const optionsQ = useDropInPaymentOptions(facilityId);
  const facilityQ = useFacilityMode(facilityId);
  const code = useRedemptionCode({ facilityId, subscriptionId });
  const { state } = code;

  const options = optionsQ.data ? canonicalizeDropInPaymentOptions(optionsQ.data) : null;
  const facilityName = options?.facility_name ?? facilityQ.data?.name ?? "";
  const totalVisits =
    options?.active_packages?.find((option) => option.subscription_id === subscriptionId)
      ?.total_visits ?? null;
  const hubPath = `/dropin/${facilityId}/check-in`;

  const notNow = async () => {
    await code.cancel();
    navigate(hubPath);
  };

  if (state.status === "checked_in") {
    return (
      <Stack spacing={1.25} sx={{ width: "100%", maxWidth: 560, minHeight: "100%" }}>
        <CheckedInView
          facilityId={facilityId}
          facilityName={facilityName}
          redemption={state.redemption}
          session={state.session}
          totalVisits={totalVisits}
        />
      </Stack>
    );
  }

  const showing = state.status === "showing" ? state : null;
  const seconds = showing ? secondsUntil(showing.refreshDueAt, code.now) : 0;
  const offline = !code.online;

  return (
    <Stack spacing={1.25} sx={{ width: "100%", maxWidth: 560, minHeight: "100%" }}>
      <DetailHeader
        title="Check-in code"
        back={{
          to: hubPath,
          label: "Back to check in",
          // Leaving by the arrow is "Not now": the open code is cancelled.
          onClick: (event) => {
            event.preventDefault();
            void notNow();
          },
        }}
      />

      <Stack spacing={0.75} sx={{ alignItems: "center", textAlign: "center", mt: 0.5 }}>
        {name ? (
          <Typography sx={{ fontSize: "1.3125rem", fontWeight: 700 }}>
            {name}{" "}
            <Box component="span" sx={{ color: "text.disabled", fontWeight: 400 }}>
              {tag}
            </Box>
          </Typography>
        ) : null}
        <Box component="span" sx={okPillSx}>
          <GroupsOutlined aria-hidden sx={{ fontSize: 15 }} />
          Drop-in · today
        </Box>

        {state.status === "starting" ? (
          <Stack
            role="status"
            spacing={1}
            sx={{ alignItems: "center", justifyContent: "center", height: 246, mt: 1 }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Making your check-in code…
            </Typography>
          </Stack>
        ) : null}

        {showing && code.passUsable ? (
          <>
            <Box
              role="img"
              aria-label="Single-use check-in QR code"
              sx={{
                p: 1.5,
                mt: 1,
                borderRadius: "20px",
                bgcolor: "common.white",
                border: 1,
                borderColor: "divider",
                boxShadow: CHECK_IN_CODE_SHADOW,
                lineHeight: 0,
                "& svg": { display: "block", width: "min(220px, 62vw)", height: "auto" },
              }}
            >
              <QrCode value={showing.pass.token} size={220} />
            </Box>
            {offline ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }} role="status">
                <WifiOffRounded aria-hidden sx={{ fontSize: 20, color: "warning.main" }} />
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
                  Offline · this code still works for a few seconds
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                <RefreshRing seconds={seconds} />
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }} aria-live="off">
                  Refreshes in{" "}
                  <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {seconds}
                  </Box>{" "}
                  s
                </Typography>
              </Box>
            )}
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              Hold it up to the scanner at the entrance
            </Typography>
          </>
        ) : null}

        {showing && !code.passUsable ? (
          <Stack role="alert" spacing={1.25} sx={{ alignItems: "center", py: 4 }}>
            <WifiOffRounded aria-hidden sx={{ fontSize: 40, color: "text.secondary" }} />
            <Typography sx={{ fontWeight: 600 }}>
              {offline ? "You're offline" : "Getting a fresh code…"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
              {offline
                ? "Reconnect to show a fresh code. Nothing is used until it is scanned."
                : "Hold on while we replace the expired code."}
            </Typography>
          </Stack>
        ) : null}

        {state.status === "ended" ? (
          <Stack role="alert" spacing={1.25} sx={{ alignItems: "center", py: 4 }}>
            <TimerOffOutlined aria-hidden sx={{ fontSize: 40, color: "text.secondary" }} />
            <Typography sx={{ fontWeight: 600 }}>This code has ended</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
              {endedMessage(state.reason)}
            </Typography>
            <Button variant="contained" disableElevation onClick={code.start} disabled={offline}>
              Get a new code
            </Button>
          </Stack>
        ) : null}

        {state.status === "failed" ? (
          <Box sx={{ width: "100%", textAlign: "left", mt: 1 }}>
            <GuardianAwareError error={state.error}>
              <FailedCode error={state.error} onRetry={code.start} hubPath={hubPath} />
            </GuardianAwareError>
          </Box>
        ) : null}
      </Stack>

      {code.redemption && state.status !== "failed" ? (
        <Box sx={{ mt: 0.5 }}>
          <WillUseCard
            redemption={code.redemption}
            totalVisits={totalVisits}
            facilityName={facilityName}
          />
        </Box>
      ) : null}

      <Box sx={{ flex: 1 }} />
      <Button
        variant="outlined"
        color="inherit"
        onClick={() => void notNow()}
        sx={{ height: 42, borderColor: "divider", fontWeight: 600 }}
      >
        Not now
      </Button>
      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", textAlign: "center" }}>
        Nothing is used until it is scanned.
      </Typography>
    </Stack>
  );
}

function FailedCode({
  error,
  onRetry,
  hubPath,
}: {
  error: unknown;
  onRetry: () => void;
  hubPath: string;
}) {
  const view = redemptionErrorView(error);
  return (
    <Alert
      severity={view.kind === "offline" ? "warning" : "error"}
      action={
        view.kind === "payment" ? (
          <Button color="inherit" size="small" component={RouterLink} to="/me/memberships">
            Memberships
          </Button>
        ) : view.retryable ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Try again
          </Button>
        ) : (
          <Button color="inherit" size="small" component={RouterLink} to={hubPath}>
            Back
          </Button>
        )
      }
    >
      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{view.title}</Typography>
      <Typography sx={{ fontSize: "0.8125rem" }}>{view.message}</Typography>
    </Alert>
  );
}
